import { AdminListGridSkeleton } from "@/components/layout/admin-widget-skeleton"
import { PaneResponsiveCardGrid } from "@/components/layout/pane-responsive-card-grid"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Pencil, Eye, Globe, Calendar } from "lucide-react"
import { AdminOrganisation } from "@/hooks/use-admin-organisations"
import { cn } from "@/lib/utils"
import { format } from "date-fns"

interface OrganisationListProps {
  organisations: AdminOrganisation[]
  isLoading: boolean
  onOrganisationClick: (organisationId: string) => void
  onEditOrganisation: (organisationId: string) => void
  highlightedOrganisationIds?: Set<string>
}

export function OrganisationList({ organisations, isLoading, onOrganisationClick, onEditOrganisation, highlightedOrganisationIds = new Set() }: OrganisationListProps) {
  if (isLoading) {
    return <AdminListGridSkeleton count={6} ariaLabel="Loading organisations" />
  }

  if (organisations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-muted-foreground mb-4">
          <Globe className="size-12 mx-auto mb-2" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No organisations found</h3>
        <p className="text-muted-foreground">
          Try creating a new organisation or adjusting your filters.
        </p>
      </div>
    )
  }

  return (
    <PaneResponsiveCardGrid>
      {organisations.map((org) => (
        <Card
          key={org.id}
          className={cn(
            "hover:shadow-md transition-shadow cursor-pointer",
            highlightedOrganisationIds.has(org.id) && "ring-2 ring-primary ring-offset-2 animate-pulse"
          )}
          onClick={() => onOrganisationClick(org.id)}
        >
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{org.name}</CardTitle>
                {org.industry && (
                  <Badge variant="secondary" className="mt-2">
                    {org.industry}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {org.description && (
              <div>
                <p className="text-sm whitespace-pre-wrap">{org.description}</p>
              </div>
            )}

            <div className="pt-2 border-t space-y-2">
              {org.website && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="size-4 text-muted-foreground" />
                  <a
                    href={org.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {org.website}
                  </a>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="size-3" />
                <span>Created: {format(new Date(org.created_at), "MMM d, yyyy")}</span>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onOrganisationClick(org.id)
              }}
            >
              <Eye className="size-4 mr-1" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onEditOrganisation(org.id)
              }}
            >
              <Pencil className="size-4 mr-1" />
              Edit
            </Button>
          </CardFooter>
        </Card>
      ))}
    </PaneResponsiveCardGrid>
  )
}

